// Prices and VAT for the self-service app (docs/superpowers/plans/2026-10-24-self-service-journey.md,
// "Decided"). One place for every figure: the app charges from it, the website shows it.
// All amounts are euro cents, excluding VAT. No onboarding fee anywhere.
import { formatEur } from "./money";

export const PRICE_CENTS = { first: 979900, second: 930900, later: 881900, enterpriseEach: 1309800 } as const;

export type PriceTier = "standard" | "enterprise";

/** The yearly price of the project at `position` (1-based). Enterprise has no discounts. */
export function projectPriceCents(position: number, tier: PriceTier): number {
  if (tier === "enterprise") return PRICE_CENTS.enterpriseEach;
  if (position <= 1) return PRICE_CENTS.first;
  return position === 2 ? PRICE_CENTS.second : PRICE_CENTS.later;
}

/** A year for `projects` projects; an organisation always pays for at least one. */
export function yearPriceCents(projects: number, tier: PriceTier): number {
  let total = 0;
  for (let i = 1; i <= Math.max(1, projects); i++) total += projectPriceCents(i, tier);
  return total;
}

const DAY = 24 * 60 * 60_000;

/** The share of a year's price for the days from `from` to `periodEnd` (a part of a day counts as a day; at most the full price). */
export function proRataCents(fullCents: number, from: Date, periodEnd: Date): number {
  const days = Math.min(365, Math.max(0, Math.ceil((periodEnd.getTime() - from.getTime()) / DAY)));
  return Math.round((fullCents * days) / 365);
}

/** The 27 EU member states (ISO 3166-1 alpha-2; Greece is GR, not EL). */
export const EU_COUNTRIES: readonly string[] = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE",
  "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
];

export const VAT_NOTES = {
  reverseCharge: "VAT reverse-charged (article 196, VAT Directive 2006/112/EC)",
  outsideEu: "Outside the scope of Dutch VAT",
} as const;

/**
 * Convenor is a Dutch company: 21% in the Netherlands; 0% (reverse charge) for a business
 * elsewhere in the EU whose VAT number VIES confirmed, 21% without one; 0% outside the EU.
 */
export function vatFor(country: string, vatValid: boolean): { rateBp: 2100 | 0; note: string | null } {
  const c = country.toUpperCase();
  if (c === "NL") return { rateBp: 2100, note: null };
  if (!EU_COUNTRIES.includes(c)) return { rateBp: 0, note: VAT_NOTES.outsideEu };
  return vatValid ? { rateBp: 0, note: VAT_NOTES.reverseCharge } : { rateBp: 2100, note: null };
}

/** The VAT on `netCents` at `rateBp` basis points (2100 = 21%), rounded to the cent. */
export const vatCents = (netCents: number, rateBp: number): number => Math.round((netCents * rateBp) / 10000);

/** 979900 -> "€ 9.799,00" */
export const formatEuro = formatEur;
